import type { Client } from '@core/domain/model';
import { failure, guard, type DataResult } from '@core/domain/result';
import { newId, toClient } from './mappers';
import type { Database } from './sqlite';

export function clientRepository(db: Database, now: () => number = Date.now) {
  return {
    async list(): Promise<Client[]> {
      const rows = await db.query(
        'SELECT * FROM clients WHERE deletedAt IS NULL ORDER BY fullName COLLATE NOCASE',
      );
      return rows.map(toClient);
    },

    async find(id: string): Promise<Client | null> {
      const rows = await db.query('SELECT * FROM clients WHERE id = ? AND deletedAt IS NULL', [id]);
      return rows[0] ? toClient(rows[0]) : null;
    },

    async create(draft: Omit<Client, 'createdAt' | 'updatedAt'> & { id?: string }) {
      return guard(async () => {
        const id = draft.id?.trim() ? draft.id : newId();
        const at = now();

        await db.run(
          `INSERT INTO clients (id, fullName, phone, email, birthDate, address, notes,
                                createdAt, updatedAt, deletedAt)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, NULL)`,
          [
            id,
            draft.fullName,
            draft.phone,
            draft.email,
            draft.birthDate,
            draft.address,
            draft.notes,
            at,
            at,
          ],
        );
        return id;
      });
    },

    async update(value: Client): Promise<DataResult<void>> {
      const exists = await db.query('SELECT id FROM clients WHERE id = ?', [value.id]);
      if (!exists[0]) return failure({ kind: 'NotFound', id: value.id });

      return guard(async () => {
        await db.run(
          `UPDATE clients SET fullName = ?, phone = ?, email = ?, birthDate = ?, address = ?,
                              notes = ?, updatedAt = ?
            WHERE id = ?`,
          [
            value.fullName,
            value.phone,
            value.email,
            value.birthDate,
            value.address,
            value.notes,
            now(),
            value.id,
          ],
        );
      });
    },

    /**
     * Мягкое удаление клиента.
     *
     * Дела при этом остаются: у них внешний ключ `ON DELETE SET NULL`, но мягкое
     * удаление строку не трогает, поэтому связь снимается явно. Дело без клиента —
     * рабочее состояние, дело с исчезнувшим клиентом — нет.
     */
    async remove(id: string): Promise<DataResult<void>> {
      const exists = await db.query('SELECT id FROM clients WHERE id = ?', [id]);
      if (!exists[0]) return failure({ kind: 'NotFound', id });

      return guard(async () => {
        const at = now();
        await db.transaction(async (tx) => {
          await tx.run('UPDATE clients SET deletedAt = ?, updatedAt = ? WHERE id = ?', [at, at, id]);
          await tx.run('UPDATE cases SET clientId = NULL, updatedAt = ? WHERE clientId = ?', [
            at,
            id,
          ]);
        });
      });
    },

    /**
     * Возврат клиента после «Отменить» ([D-12](docs/DECISIONS.md)).
     *
     * Вместе с ним возвращаются и связи: удаление сняло `clientId` у его дел, и клиент
     * без дел — это не то, что было до удаления. Отвязанные **этим** удалением дела
     * узнаются по метке времени: удаление шло одной транзакцией, и у них `updatedAt`
     * равен моменту удаления клиента. Чужие дела, отвязанные руками в другой момент,
     * так не заденешь.
     */
    async restore(id: string): Promise<DataResult<void>> {
      const [row] = await db.query<{ deletedAt: number | null }>(
        'SELECT deletedAt FROM clients WHERE id = ?',
        [id],
      );
      if (!row) return failure({ kind: 'NotFound', id });

      return guard(async () => {
        const at = now();
        const deletedAt = row.deletedAt;

        await db.transaction(async (tx) => {
          await tx.run('UPDATE clients SET deletedAt = NULL, updatedAt = ? WHERE id = ?', [at, id]);
          if (deletedAt !== null) {
            await tx.run(
              'UPDATE cases SET clientId = ?, updatedAt = ? WHERE clientId IS NULL AND updatedAt = ?',
              [id, at, deletedAt],
            );
          }
        });
      });
    },
  };
}

export type ClientRepository = ReturnType<typeof clientRepository>;
